import React from "react";
import DialogDataComponent, {executeComponentCallback} from 'Core/components/DialogDataComponent';
import PropTypes from "prop-types";
import {connect} from 'react-redux';
import * as actions from "./actions";
import {getPageActions} from 'Core/helpers/redux';
import DataValueValidation from "Core/validation";
import FormWrapper, {FormField} from "Core/components/advanced/FormWrapper";
import {isset, trimArray} from 'Core/helpers/data';
import {getDate} from "Core/helpers/datetime";
import DateInput from "Core/components/input/DateInput";
import OfficeLocationSelectInput from "Components/input/OfficeLocationSelectInput";
import TherapistSelectInput from "Components/input/TherapistSelectInput";
import TextInput from "Core/components/input/TextInput";
import TextareaInput from "Core/components/input/TextareaInput";
import {AnonymousAppointmentDataObject} from 'DataObjects/appointment';
import {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import {
	calendar_min_time_hours, icon_font_close_symbol,
	icon_font_create_symbol, icon_font_delete_symbol,
	icon_font_edit_symbol, icon_font_save_symbol
} from "Config/app";
import PatientSelectInput, {importPatientSelectOptionFromCreate} from "Components/input/PatientSelectInput";
import {FORM_FIELD_LABEL_POSITION} from "Core/components/advanced/FormWrapper/FormField";
import {ASYNC_SELECT_INPUT_TOOLBAR_POSITION} from "Core/components/input/SelectAsyncInput/const";
import PatientDialog from "Components/dialogs/PatientDialog";
import {fetchPatientRecordAction} from "Components/advanced/PatientRecord/actions";
import {isMacintosh} from "Core/helpers/system";
import PatientTherapySelectInput from "Components/input/PatientTherapySelectInput";
import {SELECT_INPUT_TOOLBAR_POSITION} from "Core/components/input/SelectInput/const";
import TherapyDialog from "Components/dialogs/TherapyDialog";
import {hideLoading} from "Core/helpers/loading";
import {fetchPatientTherapyAction} from "Components/advanced/PatientRecord/components/Therapy/actions";
import {PatientDataObject} from "DataObjects/patient";
import ConfirmDialog from "Core/components/dialogs/ConfirmDialog";
import DayTimeInput from "Components/input/DayTimeInput";
import {isBefore} from "date-fns";

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getPageActions({...actions, fetchPatientRecordAction, fetchPatientTherapyAction});

class AnonymousAppointmentDialog extends DialogDataComponent {
	constructor(props) {
		super(props, {
			data: (
				props.isNew ?
					new AnonymousAppointmentDataObject(
						null,
						(isset(props.startDate) ? new Date(props.startDate) : new Date()),
						(
							isset(props.startDate) ? 
								new Date(props.startDate) :
								getDate(new Date().setHours(calendar_min_time_hours, 0, 0, 0))
						),
						(
							isset(props.endDate) ?
								new Date(props.endDate) :
								getDate(new Date().setHours(calendar_min_time_hours + 1, 0, 0, 0))
						),
					)
					:
					null
			),

			/**
			 * Patient selected by the user to use for personalizing anonymous appointment
			 * @type {?PatientSelectOptionDataObject}
			 */
			selectedPatient: null,
			/**
			 * DB ID of the therapy selected by the user when patient is selected for personalization
			 * @type {?string}
			 */
			selectedTherapyId: null,
		}, {
			translationPath: 'AnonymousAppointmentDialog',
			enableLoadOnDataPropChange: true,
			disableLoad: props.isNew,
		});
		
		// Dialog methods
		this.openCreatePatientDialog = this.openCreatePatientDialog.bind(this);
		this.openEditPatientDialog = this.openEditPatientDialog.bind(this);
		this.openCreateTherapyDialog = this.openCreateTherapyDialog.bind(this);
		this.openEditTherapyDialog = this.openEditTherapyDialog.bind(this);
	}


	// Dialog methods ---------------------------------------------------------------------------------------------------
	/**
	 * Open the dialog to create a new patient
	 */
	openCreatePatientDialog() {
		const {openDialogAction, closeDialogAction} = this.props;
		const {selectedPatient} = this.state;
		const createDialogGUIID = 'create-patient';
		const createDialogId = 'create-patient-dialog';

		openDialogAction(createDialogGUIID, PatientDialog, {
			isNew: true,
			defaultData: (
				!selectedPatient ? 
					new PatientDataObject(
						null, 
						undefined, 
						this.getValue('firstName'), 
						'', 
						this.getValue('lastName'), 
						this.getValue('phoneNumber')
					) : undefined
			),
			dialogId: createDialogId,
			onSave: patient => {
				// Select newly created patient
				this.setState({selectedPatient: importPatientSelectOptionFromCreate(patient)}).then();
				closeDialogAction(createDialogGUIID);
			},
		}, {
			id: createDialogId,
			className: 'bordered-title',
			closeOnEscape: false,
			closeOnClickOutside: false,
			hideCloseBtn: false,
			maxWidth: 970
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), createDialogGUIID], 'left')
		);
	}

	/**
	 * Open the dialog to update the selected patient
	 */
	async openEditPatientDialog() {
		const {openDialogAction, closeDialogAction, fetchPatientRecordAction} = this.props;
		const {selectedPatient} = this.state;
		const editDialogGUIID = 'edit-patient';
		const editDialogId = 'edit-patient-dialog';
		
		/** @type {PatientRecordDataObject} */
		const patient = await this.executeAbortableAction(fetchPatientRecordAction, selectedPatient.id);

		openDialogAction(editDialogGUIID, PatientDialog, {
			isNew: false,
			title: (
				`${this.t('title')}: ` +
				`${patient?.firstName}${patient?.middleName ? ` (${patient?.middleName})` : ''} ${patient?.lastName}`
			),
			data: patient,
			dialogId: editDialogId,
			onSave: updatedPatient => {
				// Select updated patient
				this.setState({selectedPatient: importPatientSelectOptionFromCreate(updatedPatient)}).then();
				closeDialogAction(editDialogGUIID);
			},
		}, {
			id: editDialogId,
			className: 'bordered-title',
			closeOnEscape: true,
			closeOnClickOutside: false,
			hideCloseBtn: true,
			maxWidth: 970
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), editDialogGUIID], 'left')
		);
	}

	/**
	 * Open the dialog to create a new therapy for the selected patient
	 */
	openCreateTherapyDialog() {
		const {openDialogAction, showSuccessMessageAction} = this.props;
		const {selectedPatient} = this.state;

		const dialogGUIID = openDialogAction('', TherapyDialog, {
			isNew: true,
			patientId: selectedPatient.id,
			dialogId: 'create-therapy-dialog',
			onSave: therapyId => {
				showSuccessMessageAction(this.t('create_therapy_success_msg'));

				// Reload therapy select options and select the newly created one
				if (this.therapySelectRef) {
					this.therapySelectRef.reload().then(() => this.setState({selectedTherapyId: therapyId}));
				}
			},
		}, {
			id: 'create-therapy-dialog',
			className: 'bordered-title',
			closeOnEscape: true,
			closeOnClickOutside: false,
			hideCloseBtn: true,
			maxWidth: 1000
		});
		this.setOption(
			'dialogsToCloseOnUnmount',
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}

	/**
	 * Open the dialog to edit an existing therapy for the selected patient
	 */
	async openEditTherapyDialog() {
		const {openDialogAction, showSuccessMessageAction, fetchPatientTherapyAction} = this.props;
		const {selectedPatient, selectedTherapyId} = this.state;

		if (!!selectedTherapyId) {
			const loading = this.showLoading('#update-anonymous-appointment-dialog .dialog-content');
			/** @type {PatientTherapyDataObject} */
			const therapy = await this.executeAbortableAction(fetchPatientTherapyAction, selectedTherapyId);
			hideLoading(loading);

			if (isset(therapy)) {
				const dialogGUIID = openDialogAction('', TherapyDialog, {
					isNew: false,
					data: therapy,
					patientId: selectedPatient.id,
					dialogId: 'edit-therapy-dialog',
					onSave: therapyId => {
						showSuccessMessageAction(this.t('save_therapy_success_msg'));

						// Reload therapy select options and select the newly created one
						if (this.therapySelectRef) {
							this.therapySelectRef.reload().then(() => this.setState({selectedTherapyId: therapyId}));
						}
					},
				}, {
					id: 'edit-therapy-dialog',
					className: 'bordered-title',
					closeOnEscape: true,
					closeOnClickOutside: false,
					hideCloseBtn: true,
					maxWidth: 1000
				});
				this.setOption(
					'dialogsToCloseOnUnmount',
					trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
				);
			}
		}
	}


	// Validation and error methods -------------------------------------------------------------------------------------
	/**
	 * Default component's data validation method
	 *
	 * @return {boolean} True if component's data validation passed successfully.
	 */
	validate() {
		const dataValidation = new DataValueValidation();
		/** @type {AnonymousAppointmentDataObject} */
		const dataToValidate = this.getData();

		dataValidation.addRule('date', 'required', 'date');
		dataValidation.addRule('timeFrom', 'required');
		dataValidation.addRule('timeTo', 'required');
		dataValidation.addRule('officeLocationId', 'required');

		// Run validation
		const validationErrors = dataValidation.run(dataToValidate);

		if (validationErrors) this.setValidationErrors('', validationErrors).then();
		else this.clearValidationErrors().then();
		return !validationErrors;
	}

	/**
	 * Save dialog method
	 * @note This method should be called when dialog's "save" button is clicked. This method does not actually save any
	 * data to the DB or anywhere else. That should be done by the parent component.
	 */
	async save() {
		const {
			isNew, dialogGUIID, createAnonymousAppointmentAction, updateAnonymousAppointmentAction, 
			personalizeAnonymousAppointmentAction, addSuccessMessageAction
		} = this.props;
		const {selectedPatient, selectedTherapyId} = this.state;
		const data = this.getDataToReturn();

		// Do the validation
		const isValid = this.validate();

		// If validation is successful
		if (isValid) {
			const saveRes = (
				isNew ?
					await this.executeAbortableAction(createAnonymousAppointmentAction, data) :
					(!!selectedPatient ?
						await this.executeAbortableAction(
							personalizeAnonymousAppointmentAction, data, selectedPatient.id, selectedTherapyId,
						) :
						await this.executeAbortableAction(updateAnonymousAppointmentAction, data)
					)
			);
			
			if (isset(saveRes)) {
				if (isNew) addSuccessMessageAction(this.t('create_success_msg'));
				else addSuccessMessageAction(this.t('update_success_msg'));
				
				this.close();
				
				// Trigger component's 'onSave' event if appointment was saved successfully created
				executeComponentCallback(this.props.onSave, dialogGUIID);
			}
		}
	}

	/**
	 * Delete dialog method
	 */
	delete() {
		const {deleteAnonymousAppointmentAction, openDialogAction, closeDialogAction} = this.props;
		
		const dialogGUIID = openDialogAction('', ConfirmDialog, {
			message: this.t('confirm_delete', 'Appointments'),
			supportHtml: true,
			onYes: () => {
				// Close confirm dialog
				closeDialogAction(dialogGUIID);

				this.executeAbortableAction(deleteAnonymousAppointmentAction, this.getValue('id'))
					.then(res => {
						// If delete was successful
						if (isset(res)) {
							const {addSuccessMessageAction} = this.props;

							addSuccessMessageAction(this.t('delete_success_msg', 'Appointments'));

							// Close anonymous appointment dialog
							this.close();
							// Trigger component's onDelete event
							executeComponentCallback(this.props.onDelete);
						}
					});
			},
		}, {
			id: 'anonymous-appointment-delete-dialog',
			closeOnEscape: true,
			closeOnClickOutside: true,
			hideCloseBtn: true,
			maxWidth: 600
		});
		this.setOption(
			'dialogsToCloseOnUnmount', 
			trimArray([...this.getOption('dialogsToCloseOnUnmount'), dialogGUIID], 'left')
		);
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render dialog action buttons used by data dialogs
	 * @note Every dialog that is used to manipulate some data should use these action buttons on the bottom of the main
	 * render method. Standard buttons include create, save, delete, restore and close and they will be rendered
	 * depending on dialog type and available events (isNew, isRestore, onDelete, ...).
	 *
	 * @param {string|null} [createLabel] - Label used for create button. Default value will be loaded from translation file.
	 * @param {string|null} [createIcon] - Optional icon used for create button.
	 * @param {string|null} [deleteLabel] - Label used for delete button. Default value will be loaded from translation file.
	 * @param {string|null} [deleteIcon] - Optional icon used for delete button.
	 * @param {string|null} [saveLabel] - Label used for save button. Default value will be loaded from translation file.
	 * @param {string|null} [saveIcon] - Optional icon used for save button.
	 * @param {string|null} [closeLabel] - Label used for close button. Default value will be loaded from translation file.
	 * @param {string|null} [closeIcon] - Optional icon used for close button.
	 * @param {string|null} [restoreLabel] - Label used for restore button. Default value will be loaded from translation
	 * file.
	 * @param {string|null} [restoreIcon] - Optional icon used for restore button.
	 * @param {boolean} [hideClose=false] - If true, close button will not be rendered. This is useful for confirm
	 * dialogs.
	 * @return {*} Action buttons JSX to use in the main render method.
	 */
	renderDataActionButtons({
		createLabel = undefined, createIcon = undefined, deleteLabel = undefined, deleteIcon = undefined,
		saveLabel = undefined, saveIcon = undefined, closeLabel = undefined, closeIcon = undefined,
		restoreLabel = undefined, restoreIcon = undefined
	} = {}, hideClose = false) {
		const {isNew} = this.props;

		let buttons = [];

		// Close/cancel button
		if (isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		// Delete button
		if (!isNew) buttons.push({
			style: BUTTON_STYLE.ERROR,
			label: (deleteLabel ? deleteLabel : this.t('Delete', 'general')),
			icon: (isset(deleteIcon) && deleteIcon !== null ? deleteIcon : icon_font_delete_symbol),
			onClick: this.delete,
		});

		// Save buttons (create, restore and save)
		if (isNew) buttons.push({
			style: BUTTON_STYLE.SUCCESS,
			label: (createLabel ? createLabel : this.t('Create', 'general')),
			icon: (typeof createIcon === 'undefined' ? icon_font_create_symbol : createIcon),
			onClick: this.save,
			autofocus: true,
		});
		else buttons.push({
				style: BUTTON_STYLE.SUCCESS,
				label: (saveLabel ? saveLabel : this.t('Save', 'general')),
				icon: (isset(saveIcon) && saveIcon !== null ? saveIcon : icon_font_save_symbol),
				onClick: this.save,
				autofocus: true,
			});

		// Close/cancel button
		if (!isMacintosh() && !hideClose) buttons.push({
			style: BUTTON_STYLE.DEFAULT,
			label: (closeLabel ? closeLabel : this.t('Close', 'general')),
			icon: (isset(closeIcon) && closeLabel !== null ? closeIcon : icon_font_close_symbol),
			onClick: this.close,
		});

		return this.renderActionButtons(buttons);
	}
	
	render() {
		const {isNew} = this.props;
		const {selectedPatient, selectedTherapyId} = this.state;
		/** @type {AnonymousAppointmentDataObject} */
		const data = this.getData();

		return this.renderDialog(
			this.renderTitle(isNew ? this.t('title_create') : this.t('title')),
			(
				<FormWrapper className="dialog-form">
					{!isNew && !!data ?
						<div className="notice success bold">
							<FormField
								className="no-margin"
								inputClassName="full"
								labelPosition={FORM_FIELD_LABEL_POSITION.NONE} 
							>
								<PatientSelectInput
									className="anonymous-appointment-patient-search separate-toolbar-right"
									isClearable={true}
									toolbarButtons={[
										{
											position: ASYNC_SELECT_INPUT_TOOLBAR_POSITION.LEFT,
											icon: 'user',
											displayType: BUTTON_DISPLAY_TYPE.NONE,
											displayStyle: BUTTON_STYLE.SUBTLE,
											className: 'no-r-padding',
										},
										{
											position: ASYNC_SELECT_INPUT_TOOLBAR_POSITION.RIGHT,
											icon: icon_font_edit_symbol,
											tooltip: `${this.t('editPatientBtn')} ...`,
											displayType: BUTTON_DISPLAY_TYPE.TRANSPARENT,
											displayStyle: BUTTON_STYLE.ACTION,
											onClick: () => this.openEditPatientDialog(),
											hide: !selectedPatient,
										},
										{
											position: ASYNC_SELECT_INPUT_TOOLBAR_POSITION.RIGHT,
											icon: icon_font_create_symbol,
											label: this.t('createPatientBtn'),
											displayType: BUTTON_DISPLAY_TYPE.TRANSPARENT,
											displayStyle: BUTTON_STYLE.ACTION,
											onClick: () => this.openCreatePatientDialog(),
										},
									]}
									value={selectedPatient}
									onChange={p => this.setState({selectedPatient: p, selectedTherapyId: null})}
									placeholder={this.t('selectPatientPlaceholder')}
								/>
							</FormField>
						</div>
						: null
					}

					{!isNew && !!data && !!selectedPatient ?
						<FormField label={this.t('therapyIdLabel')}>
							<PatientTherapySelectInput
								className="patient-therapy-select-input separate-toolbar-right"
								simpleValue={true}
								isClearable={true}
								patientId={selectedPatient.id}
								value={selectedTherapyId}
								onChange={t => this.setState({selectedTherapyId: t})}
								toolbarButtons={[
									{
										position: ASYNC_SELECT_INPUT_TOOLBAR_POSITION.RIGHT,
										icon: icon_font_edit_symbol,
										tooltip: `${this.t('editTherapyBtn')} ...`,
										displayType: BUTTON_DISPLAY_TYPE.TRANSPARENT,
										displayStyle: BUTTON_STYLE.ACTION,
										onClick: () => this.openEditTherapyDialog(),
										hide: !selectedTherapyId,
									},
									{
										position: SELECT_INPUT_TOOLBAR_POSITION.RIGHT,
										label: this.t('createTherapyBtn'),
										icon: icon_font_create_symbol,
										displayStyle: BUTTON_STYLE.SUCCESS,
										displayType: BUTTON_DISPLAY_TYPE.TRANSPARENT,
										onClick: () => this.openCreateTherapyDialog(),
									}
								]}
								ref={node => { this.therapySelectRef = node; }}
							/>
						</FormField>
						: null
					}

					<FormField
						required={true}
						label={this.t('dateLabel')}
						errorMessages={this.getValidationErrors('date')}
					>
						<DateInput
							preventTab={true}
							value={data?.date || null}
							onChange={v => this.handleValueChange('date', v)}
						/>
					</FormField>

					<FormField
						required={true}
						label={this.t('timeFromLabel')}
						errorMessages={this.getValidationErrors('timeFrom')}
					>
						<DayTimeInput
							timeConstraints={{minutes: {step: 5}}}
							value={data?.timeFrom || null}
							onChange={v => {
								let previousTimeFrom = new Date(this.getValue('timeFrom'));
								let difference = new Date(v) - previousTimeFrom;
								this.handleValueChange('timeFrom', v)
									.then(() => {
										let newTimeTo = new Date(new Date(this.getValue('timeTo')).getTime() + difference);
										this.handleValueChange('timeTo', newTimeTo).then();
									})
							}}
						/>
					</FormField>

					<FormField
						required={true}
						label={this.t('timeToLabel')}
						errorMessages={this.getValidationErrors('timeTo')}
					>
						<DayTimeInput
							timeConstraints={{minutes: {step: 5}}}
							value={data?.timeTo || null}
							onChange={v => this.handleValueChange('timeTo', v)
								.then(() => {
									if (isBefore(this.getValue('timeTo'), this.getValue('timeFrom'))) {
										this.handleValueChange('timeTo', this.getValue('timeFrom')).then();
									}
								})
							}
						/>
					</FormField>

					{!selectedPatient ?
						<>
							<FormField
								label={this.t('firstNameLabel')}
								errorMessages={this.getValidationErrors('firstName')}
							>
								<TextInput
									name="firstName"
									value={data?.firstName || ''}
									onChange={this.handleInputChange}
									autosuggestAction={this.props.firstNameAutosuggestAction}
								/>
							</FormField>

							<FormField
								label={this.t('lastNameLabel')}
								errorMessages={this.getValidationErrors('lastName')}
							>
								<TextInput
									name="lastName"
									value={data?.lastName || ''}
									onChange={this.handleInputChange}
									autosuggestAction={this.props.lastNameAutosuggestAction}
								/>
							</FormField>
							<FormField
								label={this.t('phoneNumberLabel')}
								errorMessages={this.getValidationErrors('phoneNumber')}
							>
								<TextInput
									name="phoneNumber"
									value={data?.phoneNumber || ''}
									onChange={this.handleInputChange}
								/>
							</FormField>
						</>
						: null
					}

					<FormField
						required={true}
						label={this.t('officeLocationIdLabel')}
						errorMessages={this.getValidationErrors('officeLocationId')}
					>
						<OfficeLocationSelectInput
							isClearable={false}
							selectOnlyOptionOnLoad={isNew}
							value={data?.officeLocationId || null}
							onChange={v => this.handleValueChange('officeLocationId', v)}
						/>
					</FormField>

					<FormField
						label={this.t('therapistIdLabel')}
						errorMessages={this.getValidationErrors('therapistId')}
					>
						<TherapistSelectInput
							isClearable={true}
							selectOnlyOptionOnLoad={isNew}
							value={data?.therapistId || null}
							onChange={v => this.handleValueChange('therapistId', v)}
						/>
					</FormField>

					<FormField
						className="multiline-form-field"
						label={this.t('noteLabel')}
						errorMessages={this.getValidationErrors('note')}
					>
						<TextareaInput
							name="note"
							value={data?.note || ''}
							onChange={this.handleInputChange}
						/>
					</FormField>
				</FormWrapper>
			),
			{
				createLabel: this.t('create_btn'),
				createIcon: 'check'
			}
		);
	}
}

/**
 * Define component's own props that can be passed to it by parent components
 */
AnonymousAppointmentDialog.propTypes = {
	// Flag that specifies if this dialog is for creating a new item
	// @note This prop can be dynamically changed in the popup component.
	isNew: PropTypes.bool,
	// Appointment data if 'isNew' is false
	// @type {AnonymousAppointmentDataObject}
	data: PropTypes.object,
	// Start date and time to use when creating a new appointment ('isNew' is true)
	// @note If not specified, default start date will be used (@see 'calendar_min_time_hours' app config option).
	startDate: PropTypes.object,
	// End date and time to use when creating a new appointment ('isNew' is true)
	// @note If not specified, default start date will be used (@see 'calendar_min_time_hours' app config option).
	endDate: PropTypes.object,
	
	// Event triggered if appointment was saved successfully and dialog has already been closed
	// @param {string} dialogGUIID
	onSave: PropTypes.func,
	// Event triggered when dialog close method is called
	// @note Dialog will not be closed if event callback returns false.
	// @param {string} dialogGUIID
	onClose: PropTypes.func,
	// Event triggered after appointment has been successfully deleted
	// @note Dialog will be closed automatically.
	// no params
	onDelete: PropTypes.func,
};

export default connect(null, allActions)(AnonymousAppointmentDialog);
